import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// 2026-08 — Preferencia do usuario persistida por dispositivo. Usada para
// auto-recovery quando browser E backend perdem a subscription (Chrome/Android
// revoga silenciosamente PWAs hibernados). Sem isso, a UI mostra "desativado"
// mesmo o usuario nunca tendo clicado em desligar.
const PUSH_PREF_KEY = 'push_preference';
const setPushPref = (v) => {
  try { localStorage.setItem(PUSH_PREF_KEY, v); } catch (_) { /* privacy mode */ }
};
const getPushPref = () => {
  try { return localStorage.getItem(PUSH_PREF_KEY); } catch (_) { return null; }
};
const clearPushPref = () => {
  try { localStorage.removeItem(PUSH_PREF_KEY); } catch (_) { /* privacy mode */ }
};

// Converter base64url para Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = () => {
  const { user, authFetch } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  
  // Detecção de iOS e modo PWA (standalone)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const canEnablePushOnIOS = isIOS && isStandalone; // iOS só suporta push em modo PWA

  // Verificar suporte
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Verificar se já está inscrito + AUTO-RECOVERY (re-sincroniza com backend)
  // Roda no mount/login E quando a janela ganha foco/visibilidade (detecta revogações silenciosas)
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isSupported || !user) return;

      try {
        // Atualiza permissão atual (pode ter sido revogada silenciosamente)
        if ('Notification' in window) {
          setPermission(Notification.permission);
        }
        const registration = await navigator.serviceWorker.ready;
        const browserSub = await registration.pushManager.getSubscription();
        const browserHasSub = !!browserSub;

        // Consultar o backend para saber se ele também tem a subscription
        let backendHasSub = false;
        try {
          const res = await authFetch('/push/status', { suppressAuthRedirect: true });
          const data = res?.data || res;
          backendHasSub = !!data?.has_subscription;
        } catch (e) {
          // Falha silenciosa — segue com browser state
        }

        // Browser tem, backend não → re-envia
        if (browserHasSub && !backendHasSub && Notification.permission === 'granted') {
          try {
            console.log('[Push] Browser tem subscription mas backend não — re-sincronizando...');
            const subscriptionJson = browserSub.toJSON();
            await authFetch('/push/subscribe', {
              method: 'POST',
              data: { endpoint: subscriptionJson.endpoint, keys: subscriptionJson.keys }
            });
            setIsSubscribed(true);
            return;
          } catch (e) {
            console.warn('[Push] Falha ao re-sincronizar com backend:', e);
          }
        }

        // Backend tem, browser não → auto-re-subscribe
        if (!browserHasSub && backendHasSub && Notification.permission === 'granted') {
          try {
            console.log('[Push] Backend tem subscription mas browser não — auto-re-subscribe...');
            const registration2 = await navigator.serviceWorker.ready;
            if (VAPID_PUBLIC_KEY) {
              const newSub = await registration2.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
              });
              const newJson = newSub.toJSON();
              await authFetch('/push/subscribe', {
                method: 'POST',
                data: { endpoint: newJson.endpoint, keys: newJson.keys }
              });
              setIsSubscribed(true);
              return;
            }
          } catch (e) {
            console.warn('[Push] Auto-re-subscribe falhou:', e);
          }
        }

        // 2026-08 — 3o caminho: nem browser nem backend têm sub, mas o usuário
        // tinha ativado antes E o Chrome ainda tem permissão concedida.
        // Cenário típico: Chrome/Android revogou a sub silenciosamente E o
        // backend deletou a antiga por HTTP 410 (chegou fim de vida no FCM).
        // Sem esse branch, o toggle aparece "desligado" — o que a usuaria
        // percebe como "desligou sozinho".
        if (!browserHasSub && !backendHasSub && Notification.permission === 'granted' && getPushPref() === 'enabled') {
          try {
            console.log('[Push] Auto-recovery total: criando sub do zero baseado na preferencia salva...');
            const registration3 = await navigator.serviceWorker.ready;
            if (VAPID_PUBLIC_KEY) {
              const newSub = await registration3.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
              });
              const newJson = newSub.toJSON();
              await authFetch('/push/subscribe', {
                method: 'POST',
                data: { endpoint: newJson.endpoint, keys: newJson.keys }
              });
              setIsSubscribed(true);
              toast.success('Notificações reativadas automaticamente', {
                description: 'O navegador havia limpado a assinatura. Já corrigi para você.',
                duration: 5000
              });
              return;
            }
          } catch (e) {
            console.warn('[Push] Auto-recovery total falhou:', e);
            // Se ate essa tentativa falhar, e o usuario queria notificacao,
            // avisa com toast persistente para reativar manualmente.
            toast.warning('Suas notificações foram desativadas pelo navegador', {
              description: 'Vá em Configurações → Notificações e clique em "Ativar" novamente.',
              duration: 8000
            });
          }
        }

        setIsSubscribed(browserHasSub);
        // 2026-08 — Se o usuario tem sub ativa hoje, marca a preferencia como
        // 'enabled' (idempotente). Assim usuarios pre-existentes se beneficiam
        // do auto-recovery na proxima revogacao silenciosa pelo browser.
        if (browserHasSub && Notification.permission === 'granted' && getPushPref() !== 'enabled') {
          setPushPref('enabled');
        }
      } catch (error) {
        console.error('Erro ao verificar subscription:', error);
      }
    };

    checkSubscription();

    // Re-checa quando: (a) janela volta a ficar visível, (b) ganha foco
    // Isso detecta revogações silenciosas pelo browser sem precisar de logout/login
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkSubscription();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', checkSubscription);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', checkSubscription);
    };
  }, [isSupported, user, authFetch]);

  // Registrar Service Worker
  const registerServiceWorker = useCallback(async () => {
    if (!isSupported) return null;

    try {
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      console.log('Service Worker registrado:', registration);
      return registration;
    } catch (error) {
      console.error('Erro ao registrar Service Worker:', error);
      return null;
    }
  }, [isSupported]);

  // Solicitar permissão e inscrever
  const subscribe = useCallback(async () => {
    if (!isSupported || !user) {
      toast.error('Push notifications não suportadas neste navegador');
      return false;
    }

    setLoading(true);

    try {
      // Solicitar permissão
      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission !== 'granted') {
        toast.error('Permissão para notificações negada');
        setLoading(false);
        return false;
      }

      // Registrar service worker
      const registration = await registerServiceWorker();
      if (!registration) {
        throw new Error('Falha ao registrar Service Worker');
      }

      // Aguardar service worker estar pronto
      await navigator.serviceWorker.ready;

      // Criar subscription
      if (!VAPID_PUBLIC_KEY) {
        console.error('VAPID_PUBLIC_KEY não configurada');
        toast.error('Notificações não configuradas. Contate o administrador.');
        setLoading(false);
        return false;
      }
      
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      console.log('Push Subscription criada:', subscription);

      // Enviar subscription para o backend
      const subscriptionJson = subscription.toJSON();
      await authFetch('/push/subscribe', {
        method: 'POST',
        data: {
          endpoint: subscriptionJson.endpoint,
          keys: subscriptionJson.keys
        }
      });

      setIsSubscribed(true);
      setPushPref('enabled'); // 2026-08 — permite auto-recovery se o browser revogar
      toast.success('Notificações ativadas com sucesso!');
      return true;

    } catch (error) {
      console.error('Erro ao ativar notificações:', error);
      toast.error('Erro ao ativar notificações');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, user, authFetch, registerServiceWorker]);

  // Cancelar inscrição
  const unsubscribe = useCallback(async () => {
    if (!isSupported) return false;

    setLoading(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Remover do backend
        try {
          await authFetch('/push/unsubscribe', {
            method: 'POST',
            data: {
              endpoint: subscription.endpoint
            }
          });
        } catch (e) {
          console.warn('Erro ao remover subscription do backend:', e);
        }

        // Cancelar localmente
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      clearPushPref(); // 2026-08 — respeitar desligamento explicito (nao re-ativar)
      toast.success('Notificações desativadas');
      return true;

    } catch (error) {
      console.error('Erro ao desativar notificações:', error);
      toast.error('Erro ao desativar notificações');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, authFetch]);

  // Toggle subscription
  const toggleSubscription = useCallback(async () => {
    if (isSubscribed) {
      return await unsubscribe();
    } else {
      return await subscribe();
    }
  }, [isSubscribed, subscribe, unsubscribe]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe,
    toggleSubscription,
    // Novas propriedades para suporte iOS PWA
    isIOS,
    isStandalone,
    canEnablePushOnIOS
  };
};

export default usePushNotifications;
